import Skeleton from "@mui/material/Skeleton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PathBackButton } from "src/components/Default/LekUI/BackButton";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { UploadPhotoModal } from "src/components/NewComponents/Modals/UploadFiles/UploadPhotoModal";
import TipTapEditor from "src/components/TipTapEditor/TipTapEditor";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import { useGetUserNote } from "src/hooks/useFolders";
import newApi from "src/newApi";
import "src/styles/notes.scss";
import * as Yup from "yup";

const buildNoteHtml = (htmlString, images) => {
	if (images.length === 0) return htmlString;

	const imagesHtml = images
		.map((img) => `<img src="${img.image.path}" alt="${img.image.name}" />`)
		.join("");

	return `${htmlString}${imagesHtml}`;
};

export const NoteView = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { id: folderId, noteId } = useParams();
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [editorError, setEditorError] = useState("");
	const queryClient = useQueryClient();

	const isEditMode = !!noteId;
	const { data: noteData, isLoading } = useGetUserNote(noteId);

	const mutation = useMutation({
		mutationFn: ({ note, name }) => {
			if (isEditMode) {
				return newApi.updateUserNote(noteId, { note, name });
			}
			return newApi.createUserNote({
				note,
				name,
				folderId: +folderId,
			});
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["user-note"],
			});
			queryClient.invalidateQueries({
				queryKey: ["user-notes-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder"],
			});
			history.goBack();
		},

		onError: () => {
			toast.error(t("note.saveError", "Nie udało się zapisać notatki"));
		},
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			title: noteData?.name || "",
			content: "",
			images: [],
		},
		validationSchema: Yup.object().shape({
			title: Yup.string()
				.required(t("validation.required", "To pole jest wymagane"))
				.max(255, t("validation.maxChars", "Maksymalnie 255 znaków")),
		}),
		onSubmit: (values) => {
			const note = buildNoteHtml(values.content, values.images);
			mutation.mutate({ note, name: values.title });
		},
	});

	const handleSave = async (htmlString) => {
		formik.setFieldTouched("title", true);
		const errors = await formik.validateForm();
		if (errors.title) return;

		const isEmpty =
			!htmlString || htmlString.replace(/<[^>]*>/g, "").trim() === "";
		if (isEmpty) {
			setEditorError(
				t("note.emptyContent", "Treść notatki nie może być pusta"),
			);
			return;
		}

		mutation.mutate({ note: htmlString, name: formik.values.title });
	};

	const handleCancel = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			history.push("/");
		}
	};

	const handleImageUpload = (image) => {
		formik.setFieldValue("images", [...formik.values.images, image]);
		setIsUploadModalOpen(false);
	};

	if (isEditMode && isLoading) {
		return (
			<LayoutWithSearch>
				<div className="flex justify-center pb-24">
					<div className="w-100">
						<div className="note-view-card">
							<div className="mb-16">
								<Skeleton
									variant="text"
									width={120}
									height={20}
									className="mb-8"
								/>
								<Skeleton variant="rounded" width={350} height={48} />
							</div>
							<div className="border-top-1 my-16" />
							<Skeleton variant="rounded" height={44} className="mb-16" />
							<Skeleton variant="rounded" height={300} />
						</div>
					</div>
				</div>
			</LayoutWithSearch>
		);
	}

	return (
		<LayoutWithSearch>
			<div className="flex justify-center pb-24">
				<div className="w-100">
					<div className="note-view-card">
						<div className="mb-16">
							<div className="flex items-center gap-8 mb-8">
								<PathBackButton />
								<span className="text-sm font-medium">
									{t("note.titleLabel", "Nazwa notatki")}
								</span>
							</div>
							<TextFieldGrey
								variant="filled"
								name="title"
								data-test-id="note-title-input"
								value={formik.values.title}
								onChange={formik.handleChange}
								className="w-100 max-w-350"
								autoFocus
								error={formik.touched.title && Boolean(formik.errors.title)}
								helperText={formik.touched.title && formik.errors.title}
								slotProps={{ htmlInput: { maxLength: 255 } }}
							/>
						</div>
						<div className="border-top-1 my-16" />
						<TipTapEditor
							onSubmit={handleSave}
							submitText={t("note.save", "Zapisz zmiany")}
							cancelText={t("note.cancel", "Anuluj")}
							onCancel={handleCancel}
							className="note-editor"
							initialHtml={noteData?.note || ""}
							error={!!editorError}
							helperText={editorError}
							onUpdate={() => {
								if (editorError) setEditorError("");
							}}
						/>
					</div>
				</div>
			</div>
			{isUploadModalOpen && (
				<UploadPhotoModal
					onClose={() => setIsUploadModalOpen(false)}
					onConfirm={handleImageUpload}
				/>
			)}
		</LayoutWithSearch>
	);
};
